import type { AssignedChatDetails } from "@/types/assignedChatDetails.ts";
import { create } from "zustand/react";
import { interventionStorageCollection } from "@/collections/interventionStorageCollection.ts";
import { useSettingsStore } from "@/stores/useSettingsStore.ts";
import { PlayAudio } from "@/helpers/audio.ts";
import audioHelp1 from "@/assets/audio/helprequest_1.mp3";
import { metrics } from "@sentry/react";
import audioHelp2 from "@/assets/audio/helprequest_2.mp3";

export const DefaultSession: AssignedSession = Object.freeze({
  assignedChat: {
    flowGeneratorSessionId: 0,
    flowGeneratorSessionInterventionId: 0,
    dateTutorJoined: new Date(),
    tutorId: 0,
    tutorUserId: 0,
  },
  completed: false,
  feedbackGiven: false,
});

export interface AssignedSession {
  assignedChat: AssignedChatDetails;
  completed: boolean;
  feedbackGiven: boolean;
}

export type UseAssignedChatStore = {
  assignedSessions: Map<number, AssignedSession>;
  AddNewSession: (session: Partial<AssignedSession>) => void;
  UpdateSessionDetails: (
    flowGeneratorSessionId: number,
    assignedChat: Partial<AssignedChatDetails>,
  ) => void;
  RemoveSession: (flowGeneratorSessionId: number) => void;
  SetSessionCompleted: (flowGeneratorSessionId: number) => void;
  SetSessionFeedbackGiven: (flowGeneratorSessionId: number) => void;
};

export const useAssignedChatStore = create<UseAssignedChatStore>((set) => ({
  assignedSessions: new Map<number, AssignedSession>(),

  AddNewSession: (session: Partial<AssignedSession>) =>
    set((state) => {
      const newSession = { ...DefaultSession, ...session };
      const flowGeneratorSessionId = newSession.assignedChat.flowGeneratorSessionId;

      // Play audio if we have just been assigned a new session
      if (!session.completed) {
        if (useSettingsStore.getState().settings.AudioNewSession) {
          if (Math.random() < 0.5) {
            PlayAudio(audioHelp1).catch((e) => {
              metrics.count("audio_notification_failure", 1);
              console.error("Failed to play audio note 1:", e);
            });
          } else {
            PlayAudio(audioHelp2).catch((e) => {
              metrics.count("audio_notification_failure", 1);
              console.error("Failed to play audio note 2:", e);
            });
          }
        }
      }

      const assignedSessions = new Map(state.assignedSessions);
      assignedSessions.set(flowGeneratorSessionId, newSession);
      return { assignedSessions };
    }),

  UpdateSessionDetails: (
    flowGeneratorSessionId: number,
    assignedChat: Partial<AssignedChatDetails>,
  ) =>
    set((state) => {
      const current = state.assignedSessions.get(flowGeneratorSessionId);
      if (!current) return state;

      const assignedSessions = new Map(state.assignedSessions);
      assignedSessions.set(flowGeneratorSessionId, {
        ...current,
        assignedChat: { ...current.assignedChat, ...assignedChat },
      });

      return { assignedSessions };
    }),

  RemoveSession: (flowGeneratorSessionId: number) =>
    set((state) => {
      if (!state.assignedSessions.has(flowGeneratorSessionId)) return state;

      const assignedSessions = new Map(state.assignedSessions);
      assignedSessions.delete(flowGeneratorSessionId);

      if (interventionStorageCollection.get(flowGeneratorSessionId)) {
        interventionStorageCollection.delete(flowGeneratorSessionId);
      }

      return { assignedSessions };
    }),

  SetSessionCompleted: (flowGeneratorSessionId: number) =>
    set((state) => {
      const current = state.assignedSessions.get(flowGeneratorSessionId);
      if (!current) return state;

      const assignedSessions = new Map(state.assignedSessions);
      assignedSessions.set(flowGeneratorSessionId, {
        ...current,
        completed: true,
      });

      return { assignedSessions };
    }),

  SetSessionFeedbackGiven: (flowGeneratorSessionId: number) =>
    set((state) => {
      const current = state.assignedSessions.get(flowGeneratorSessionId);
      if (!current) return state;

      const assignedSessions = new Map(state.assignedSessions);
      assignedSessions.set(flowGeneratorSessionId, {
        ...current,
        feedbackGiven: true,
      });

      interventionStorageCollection.update(flowGeneratorSessionId, (draft) => {
        draft.isFeedbackComplete = true;
      });

      return { assignedSessions };
    }),
}));
